/**
 * Venn diagrams: where the circles go, which region a point or a label falls in, how a
 * region's items are fitted inside it, and the set arithmetic behind the counts.
 *
 * The diagram is the classic textbook kind — equal circles in fixed positions, with
 * every region drawn even when it is empty, so a reader can see that "in A and B but
 * not C" holds nothing. A diagram whose circle sizes and overlaps are scaled to the
 * counts is an area-proportional (Euler) diagram, a different figure.
 *
 * Text placement is checked exactly rather than estimated: a label block is a
 * rectangle, a rectangle lies inside a circle when its four corners do (a disc is
 * convex), and it stays clear of a circle when its nearest point to the centre is at
 * least a radius away. So "every label sits inside its own region" is a property the
 * tests can assert, not a hope.
 */

import {
  CHART_PADDING,
  LINE_HEIGHT,
  layoutTitle,
  type Rect,
  type TitleBlock,
} from "@/lib/tool-kit/charts/layout";
import {
  maxTextWidth,
  type TextStyle,
  truncateText,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import { rectsOverlap } from "@/lib/tool-kit/diagrams/geometry";

export type SetCount = 2 | 3;

export type RegionId = "a" | "b" | "c" | "ab" | "ac" | "bc" | "abc";

/** The regions of a two- or three-set diagram, in reading order. */
export const REGION_IDS: Record<SetCount, readonly RegionId[]> = {
  2: ["a", "ab", "b"],
  3: ["a", "b", "c", "ab", "ac", "bc", "abc"],
};

/** The sets (0 = A, 1 = B, 2 = C) each region is inside. */
export const REGION_SETS: Record<RegionId, readonly number[]> = {
  a: [0],
  b: [1],
  c: [2],
  ab: [0, 1],
  ac: [0, 2],
  bc: [1, 2],
  abc: [0, 1, 2],
};

export interface Circle {
  cx: number;
  cy: number;
  r: number;
}

export const VENN_WIDTH = 640;

/** Radius and centre distance. Equal distance and radius: each circle passes through
 * the other's centre, which gives the overlap and the crescents the same width. */
const TWO_SET = { r: 150, d: 150 };
/** Three circles on an equilateral triangle, slightly wider than the radius so the
 * two-set regions have room for a few words. */
const THREE_SET = { r: 128, d: 142 };

/** Text keeps this far from any circle outline. */
const TEXT_MARGIN = 4;
const NAME_FONT = 14;
const UNIVERSE_FONT = 13;
const ITEM_FONT = { max: 13, min: 9 };
const COUNT_FONT = { max: 22, min: 11 };

/* ------------------------------------------------------------ geometry */

/** The circles, with the top of the topmost ones at `top`. */
export function vennCircles(
  setCount: SetCount,
  centerX: number,
  top: number
): Circle[] {
  if (setCount === 2) {
    const { r, d } = TWO_SET;
    return [
      { cx: centerX - d / 2, cy: top + r, r },
      { cx: centerX + d / 2, cy: top + r, r },
    ];
  }
  const { r, d } = THREE_SET;
  const h = (d * Math.sqrt(3)) / 2;
  return [
    { cx: centerX - d / 2, cy: top + r, r },
    { cx: centerX + d / 2, cy: top + r, r },
    { cx: centerX, cy: top + r + h, r },
  ];
}

function insideCircle(circle: Circle, x: number, y: number): boolean {
  return Math.hypot(x - circle.cx, y - circle.cy) <= circle.r;
}

/** The region a point falls in, or null outside every circle. */
export function regionAt(
  x: number,
  y: number,
  circles: readonly Circle[]
): RegionId | null {
  const key = circles
    .map((circle, index) => (insideCircle(circle, x, y) ? "abc"[index] : ""))
    .join("");
  return key === "" ? null : (key as RegionId);
}

function rectInsideCircle(rect: Rect, circle: Circle, margin: number) {
  const limit = circle.r - margin;
  for (const x of [rect.x, rect.x + rect.width]) {
    for (const y of [rect.y, rect.y + rect.height]) {
      if (Math.hypot(x - circle.cx, y - circle.cy) > limit) {
        return false;
      }
    }
  }
  return true;
}

function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

/** True when no point of the rectangle comes within `margin` of the disc. */
export function rectClearOfCircle(
  rect: Rect,
  circle: Circle,
  margin = 0
): boolean {
  const nearestX = clamp(circle.cx, rect.x, rect.x + rect.width);
  const nearestY = clamp(circle.cy, rect.y, rect.y + rect.height);
  return (
    Math.hypot(nearestX - circle.cx, nearestY - circle.cy) >= circle.r + margin
  );
}

/** True when the whole rectangle lies inside region `id` (with a margin to spare). */
export function rectInRegion(
  rect: Rect,
  circles: readonly Circle[],
  id: RegionId,
  margin = TEXT_MARGIN
): boolean {
  const members = REGION_SETS[id];
  return circles.every((circle, index) =>
    members.includes(index)
      ? rectInsideCircle(rect, circle, margin)
      : rectClearOfCircle(rect, circle, margin)
  );
}

interface Point {
  x: number;
  y: number;
}

const placeCache = new Map<SetCount, Map<RegionId, Point[]>>();

/** Clearance from every outline: how far a point is from the nearest circle's edge. */
function clearance(x: number, y: number, circles: readonly Circle[]): number {
  return Math.min(
    ...circles.map((c) => Math.abs(Math.hypot(x - c.cx, y - c.cy) - c.r))
  );
}

/**
 * Where a region's text may be centred, best first.
 *
 * The first candidate is the region's pole — the point farthest from every outline,
 * found on a 2-unit grid. The rest are the region's other points on a 4-unit grid within
 * 72 units of it, nearest first: a crescent holds a wide block better a little off its
 * pole, and trying nearby centres finds that without a special case per region.
 * Computed once per diagram shape, relative to `vennCircles(n, 0, 0)`.
 */
function regionPlaces(setCount: SetCount): Map<RegionId, Point[]> {
  const cached = placeCache.get(setCount);
  if (cached) {
    return cached;
  }
  const circles = vennCircles(setCount, 0, 0);
  const places = new Map<RegionId, Point[]>();
  for (const [id, pole] of regionPoles(circles)) {
    places.set(id, [pole, ...nearbyPoints(pole, id, circles)]);
  }
  placeCache.set(setCount, places);
  return places;
}

/** Each region's pole: its point farthest from every outline, on a 2-unit grid. */
function regionPoles(circles: readonly Circle[]): Map<RegionId, Point> {
  const left = Math.min(...circles.map((c) => c.cx - c.r));
  const right = Math.max(...circles.map((c) => c.cx + c.r));
  const bottom = Math.max(...circles.map((c) => c.cy + c.r));
  const best = new Map<RegionId, Point & { clearance: number }>();
  for (let y = 0; y <= bottom; y += 2) {
    for (let x = left; x <= right; x += 2) {
      const id = regionAt(x, y, circles);
      const room = clearance(x, y, circles);
      const current = id ? best.get(id) : undefined;
      if (id && (!current || room > current.clearance)) {
        best.set(id, { x, y, clearance: room });
      }
    }
  }
  const poles = new Map<RegionId, Point>();
  for (const [id, pole] of best) {
    poles.set(id, { x: pole.x, y: pole.y });
  }
  return poles;
}

/** The region's other points within 72 units of `pole`, on a 4-unit grid, nearest first. */
function nearbyPoints(
  pole: Point,
  id: RegionId,
  circles: readonly Circle[]
): Point[] {
  const nearby: (Point & { distance: number })[] = [];
  for (let dy = -72; dy <= 72; dy += 4) {
    for (let dx = -72; dx <= 72; dx += 4) {
      const x = pole.x + dx;
      const y = pole.y + dy;
      if ((dx !== 0 || dy !== 0) && regionAt(x, y, circles) === id) {
        nearby.push({ x, y, distance: Math.hypot(dx, dy) });
      }
    }
  }
  nearby.sort((p, q) => p.distance - q.distance);
  return nearby.map(({ x, y }) => ({ x, y }));
}

/* ------------------------------------------------------------ text fitting */

export interface TextBlock {
  lines: string[];
  /** Top of each line, from the top of the block (items are spaced a little apart). */
  tops: number[];
  fontSize: number;
  lineHeight: number;
  bold: boolean;
  /** Centre of the block. */
  cx: number;
  cy: number;
  width: number;
  height: number;
  /** Items left out for lack of room (they are listed in the data table). */
  hidden: number;
}

export function blockRect(block: TextBlock): Rect {
  return {
    x: block.cx - block.width / 2,
    y: block.cy - block.height / 2,
    width: block.width,
    height: block.height,
  };
}

interface FitOptions {
  maxFont: number;
  minFont: number;
  maxWidth: number;
  bold: boolean;
  /** "+3 more" — the line that stands in for items that do not fit. */
  more: (hidden: number) => string;
}

const WIDTH_FACTORS = [1, 0.85, 0.7, 0.55, 0.42];
/** An item may wrap onto this many lines before it counts as not fitting. */
const MAX_ITEM_LINES = 3;
const WORD_GAP = /\s+/;

/**
 * The first candidate centre where a block of `lines` fits, as a placed block.
 */
/** Extra space between one item and the next, as a share of the type size. */
const ITEM_GAP = 0.4;

/** Line tops for groups of lines (one group per item), and the block's height. */
function stack(groups: readonly string[][], fontSize: number) {
  const lineHeight = fontSize * LINE_HEIGHT;
  const tops: number[] = [];
  let y = 0;
  for (const [index, group] of groups.entries()) {
    if (index > 0) {
      y += fontSize * ITEM_GAP;
    }
    for (const _ of group) {
      tops.push(y);
      y += lineHeight;
    }
  }
  return { tops, height: y, lineHeight };
}

/**
 * The first candidate centre where a block of `groups` (each item's lines) fits, as a
 * placed block.
 */
function placeBlock(
  groups: string[][],
  places: readonly Point[],
  fits: (rect: Rect) => boolean,
  style: TextStyle,
  hidden: number
): TextBlock | null {
  const lines = groups.flat();
  const { tops, height, lineHeight } = stack(groups, style.fontSize);
  const width = Math.ceil(maxTextWidth(lines, style));
  for (const place of places) {
    const block: TextBlock = {
      lines,
      tops,
      fontSize: style.fontSize,
      lineHeight,
      bold: style.bold ?? false,
      cx: place.x,
      cy: place.y,
      width,
      height,
      hidden,
    };
    if (fits(blockRect(block))) {
      return block;
    }
  }
  return null;
}

/**
 * Items wrapped to `width`, or null when one of them would have to be cut: a word wider
 * than the line, or more than `MAX_ITEM_LINES` lines.
 */
function wrapWhole(
  items: readonly string[],
  width: number,
  style: TextStyle
): string[][] | null {
  const groups: string[][] = [];
  for (const item of items) {
    const words = item.split(WORD_GAP);
    if (words.some((word) => maxTextWidth([word], style) > width)) {
      return null;
    }
    const wrapped = wrapText(item, width, style);
    if (wrapped.length > MAX_ITEM_LINES) {
      return null;
    }
    groups.push(wrapped);
  }
  return groups;
}

function fitWhole(
  items: readonly string[],
  places: readonly Point[],
  fits: (rect: Rect) => boolean,
  options: FitOptions
): TextBlock | null {
  for (let font = options.maxFont; font >= options.minFont; font--) {
    const style: TextStyle = { fontSize: font, bold: options.bold };
    for (const factor of WIDTH_FACTORS) {
      const groups = wrapWhole(
        items,
        Math.max(36, options.maxWidth * factor),
        style
      );
      const block = groups && placeBlock(groups, places, fits, style, 0);
      if (block) {
        return block;
      }
    }
  }
  return null;
}

/**
 * Fit a region's items inside it: the largest type size at which every item fits whole,
 * trying narrower lines before smaller type and centres near the region's middle. When
 * even the smallest size cannot hold them all, the first items are shown — each cut to
 * two lines at most — with a "+n more" line for the rest.
 */
export function fitItems(
  items: readonly string[],
  places: readonly Point[],
  fits: (rect: Rect) => boolean,
  options: FitOptions
): TextBlock | null {
  if (items.length === 0) {
    return null;
  }
  const whole = fitWhole(items, places, fits, options);
  if (whole) {
    return whole;
  }
  const style: TextStyle = { fontSize: options.minFont, bold: options.bold };
  const attempt = (shown: number): TextBlock | null => {
    const hidden = items.length - shown;
    for (const factor of WIDTH_FACTORS) {
      const width = Math.max(36, options.maxWidth * factor);
      const groups = [
        ...items.slice(0, shown).map((item) => wrapText(item, width, style, 2)),
        [options.more(hidden)],
      ];
      const block = placeBlock(groups, places, fits, style, hidden);
      if (block) {
        return block;
      }
    }
    return null;
  };
  // Fewer items always take less room, so the most that fit can be found by halving.
  let best = attempt(0);
  let low = 1;
  let high = items.length - 1;
  while (best && low <= high) {
    const middle = Math.floor((low + high) / 2);
    const block = attempt(middle);
    if (block) {
      best = block;
      low = middle + 1;
    } else {
      high = middle - 1;
    }
  }
  return best;
}

/* ---------------------------------------------------------------- layout */

export interface NameLabel {
  lines: string[];
  /** Centre of the lines. */
  x: number;
  /** Top of the block. */
  y: number;
  fontSize: number;
  lineHeight: number;
  rect: Rect;
}

export interface VennUniverse {
  rect: Rect;
  label: string;
  /** Baseline of the label, at the rectangle's top left. */
  labelX: number;
  labelY: number;
  outside: TextBlock | null;
}

export interface VennInput {
  setCount: SetCount;
  /** The set names, A first. */
  names: readonly string[];
  /** What to write in each region: its items, or one formatted count. */
  regions: Partial<Record<RegionId, readonly string[]>>;
  /** Counts are drawn larger and bold. */
  counts: boolean;
  /** The universal set's rectangle and what lies outside every circle. */
  universe: { label: string; outside: readonly string[] } | null;
  /** Drawn inside the image; empty for none (an APA figure). */
  title: string;
  more: (hidden: number) => string;
}

export interface VennLayout {
  width: number;
  height: number;
  title: TitleBlock | null;
  circles: Circle[];
  names: NameLabel[];
  regions: { id: RegionId; block: TextBlock | null }[];
  universe: VennUniverse | null;
}

function nameLabel(
  name: string,
  circle: Circle,
  span: [number, number],
  top: number
): NameLabel {
  const style: TextStyle = { fontSize: NAME_FONT, bold: true };
  const maxWidth = Math.min(2 * circle.r, span[1] - span[0]);
  const lines = wrapText(name, maxWidth, style, 2);
  const width = Math.ceil(maxTextWidth(lines, style));
  const lineHeight = NAME_FONT * LINE_HEIGHT;
  const x = clamp(circle.cx, span[0] + width / 2, span[1] - width / 2);
  const height = lines.length * lineHeight;
  return {
    lines,
    x,
    y: top,
    fontSize: NAME_FONT,
    lineHeight,
    rect: { x: x - width / 2, y: top, width, height },
  };
}

function nameHeight(label: NameLabel | undefined): number {
  return label ? label.lines.length * label.lineHeight : 0;
}

function regionBlocks(
  input: VennInput,
  circles: Circle[],
  originX: number,
  originY: number
): { id: RegionId; block: TextBlock | null }[] {
  const places = regionPlaces(input.setCount);
  const font = input.counts ? COUNT_FONT : ITEM_FONT;
  return REGION_IDS[input.setCount].map((id) => {
    const items = input.regions[id] ?? [];
    const block = fitItems(
      items,
      (places.get(id) ?? []).map((place) => ({
        x: place.x + originX,
        y: place.y + originY,
      })),
      (rect) => rectInRegion(rect, circles, id),
      {
        maxFont: font.max,
        minFont: font.min,
        maxWidth: input.setCount === 2 ? 132 : 112,
        bold: input.counts,
        more: input.more,
      }
    );
    return { id, block };
  });
}

/**
 * What lies outside every circle, as a block in the universal set's bottom-right corner.
 * It goes beside the circles when that corner is clear of them and of the set names, and
 * in a band of its own below everything otherwise — never where it could be read as part
 * of a set.
 */
function outsideBlock(
  items: readonly string[],
  counts: boolean,
  frame: { right: number; bottom: number; width: number },
  avoid: { circles: readonly Circle[]; names: readonly NameLabel[] }
): { block: TextBlock; below: boolean } | null {
  if (items.length === 0) {
    return null;
  }
  const font = counts ? 16 : 12;
  const style: TextStyle = { fontSize: font, bold: counts };
  const lines = wrapText(items.join(", "), frame.width / 2 - 24, style, 3);
  const lineHeight = font * LINE_HEIGHT;
  const width = Math.ceil(maxTextWidth(lines, style));
  const height = lines.length * lineHeight;
  const block = (bottom: number): TextBlock => ({
    lines,
    tops: lines.map((_, index) => index * lineHeight),
    fontSize: font,
    lineHeight,
    bold: counts,
    cx: frame.right - 12 - width / 2,
    cy: bottom - height / 2,
    width,
    height,
    hidden: 0,
  });
  const corner = block(frame.bottom);
  const area = blockRect(corner);
  const clear =
    avoid.circles.every((circle) => rectClearOfCircle(area, circle, 8)) &&
    avoid.names.every((name) => !rectsOverlap(area, name.rect, 8));
  if (clear) {
    return { block: corner, below: false };
  }
  return { block: block(frame.bottom + 10 + height), below: true };
}

/**
 * Everything on a Venn diagram, placed: title, the universal set's rectangle and label,
 * the set names (above the circles, and below for the third), the circles, and each
 * region's text. The image is 640 units wide and as tall as its contents.
 */
export function layoutVenn(input: VennInput): VennLayout {
  const width = VENN_WIDTH;
  const title = layoutTitle(input.title, width);
  let top = CHART_PADDING + (title?.height ?? 0);
  const universeTop = top;
  const frameLeft = CHART_PADDING + (input.universe ? 10 : 0);
  const frameRight = width - CHART_PADDING - (input.universe ? 10 : 0);
  if (input.universe) {
    top += 8 + UNIVERSE_FONT * LINE_HEIGHT + 8;
  }
  const centerX = width / 2;
  // Horizontal positions do not depend on `top`, so the names can be measured first.
  const probe = vennCircles(input.setCount, centerX, 0);
  const middle = (probe[0].cx + probe[1].cx) / 2;
  const topSpans: [number, number][] = [
    [frameLeft, middle - 6],
    [middle + 6, frameRight],
  ];
  const names = [0, 1].map((index) =>
    nameLabel(input.names[index] ?? "", probe[index], topSpans[index], top)
  );
  top += Math.max(...names.map(nameHeight)) + 8;
  const circles = vennCircles(input.setCount, centerX, top);
  top = Math.max(...circles.map((c) => c.cy + c.r));
  if (input.setCount === 3) {
    const bottomName = nameLabel(
      input.names[2] ?? "",
      circles[2],
      [frameLeft, frameRight],
      top + 8
    );
    names.push(bottomName);
    top += 8 + nameHeight(bottomName);
  }
  const regions = regionBlocks(
    input,
    circles,
    centerX,
    circles[0].cy - circles[0].r
  );
  let universe: VennUniverse | null = null;
  if (input.universe) {
    const rectX = CHART_PADDING;
    const rectWidth = width - 2 * CHART_PADDING;
    const outside = outsideBlock(
      input.universe.outside,
      input.counts,
      { right: rectX + rectWidth, bottom: top, width: rectWidth },
      { circles, names }
    );
    if (outside?.below) {
      top += 10 + outside.block.height;
    }
    const rect = {
      x: rectX,
      y: universeTop,
      width: rectWidth,
      height: top + 12 - universeTop,
    };
    top = rect.y + rect.height;
    universe = {
      rect,
      label: truncateText(input.universe.label, rect.width - 20, {
        fontSize: UNIVERSE_FONT,
        bold: true,
      }),
      labelX: rect.x + 10,
      labelY: rect.y + 8 + UNIVERSE_FONT,
      outside: outside?.block ?? null,
    };
  }
  return {
    width,
    height: Math.ceil(top + CHART_PADDING),
    title,
    circles,
    names,
    regions,
    universe,
  };
}

/* -------------------------------------------------------------- set logic */

const LINE_BREAK = /\r\n|\r|\n/;
const LIST_BREAK = /\r\n|\r|\n|[,;]/;
const SPACE_RUN = /\s+/g;

/**
 * Items typed into a box: one per line, or — for set members, which are short — also
 * separated by commas or semicolons. Blank entries are dropped.
 */
export function splitItems(
  text: string,
  separators: "lines" | "list"
): string[] {
  return text
    .split(separators === "lines" ? LINE_BREAK : LIST_BREAK)
    .map((item) => item.replace(SPACE_RUN, " ").trim())
    .filter((item) => item !== "");
}

function itemKey(item: string): string {
  return item.toLowerCase();
}

/** Each set's members without repeats, matched without regard to capitals. */
function uniqueMembers(members: readonly string[]): string[] {
  const seen = new Set<string>();
  const unique: string[] = [];
  for (const item of members) {
    const key = itemKey(item);
    if (!seen.has(key)) {
      seen.add(key);
      unique.push(item);
    }
  }
  return unique;
}

export interface SortedMembers {
  regions: Record<RegionId, string[]>;
  /** Members of the universal set that are in none of the sets. */
  outside: string[];
  /** Set members missing from the universal set (when one was given). */
  notInUniverse: string[];
  /** How many members each set has, after removing repeats. */
  setSizes: number[];
}

function emptyRegions(): Record<RegionId, string[]> {
  return { a: [], b: [], c: [], ab: [], ac: [], bc: [], abc: [] };
}

/**
 * Sort the members of two or three sets into the diagram's regions — the set logic a
 * Venn diagram draws. An element listed in A and in B lands in "A and B only"; one in all
 * three lands in the centre. Matching ignores capitals and keeps the first spelling.
 */
export function sortIntoRegions(
  sets: readonly (readonly string[])[],
  universe: readonly string[] | null
): SortedMembers {
  const regions = emptyRegions();
  const lists = sets.map(uniqueMembers);
  const keys = lists.map((list) => new Set(list.map(itemKey)));
  const placed = new Set<string>();
  for (const list of lists) {
    for (const item of list) {
      const key = itemKey(item);
      if (placed.has(key)) {
        continue;
      }
      placed.add(key);
      const id = keys
        .map((set, index) => (set.has(key) ? "abc"[index] : ""))
        .join("") as RegionId;
      regions[id].push(item);
    }
  }
  const universeList = universe ? uniqueMembers(universe) : [];
  const universeKeys = new Set(universeList.map(itemKey));
  const notInUniverse: string[] = [];
  if (universe) {
    const reported = new Set<string>();
    for (const item of lists.flat()) {
      const key = itemKey(item);
      if (!(universeKeys.has(key) || reported.has(key))) {
        reported.add(key);
        notInUniverse.push(item);
      }
    }
  }
  return {
    regions,
    outside: universeList.filter((item) => !placed.has(itemKey(item))),
    notInUniverse,
    setSizes: lists.map((list) => list.length),
  };
}

export interface SetTotals {
  /** n(A), n(B), n(C): everything inside each circle. */
  sets: number[];
  /** n(A ∩ B), n(A ∩ C), n(B ∩ C): everything in both, the centre included. */
  pairs: { ab: number; ac: number; bc: number };
  /** n(A ∩ B ∩ C). */
  all: number;
  /** n(A ∪ B ∪ C): everything inside at least one circle. */
  union: number;
}

/** The set sizes behind the inclusion–exclusion working, from the region counts. */
export function setTotals(
  setCount: SetCount,
  counts: Partial<Record<RegionId, number>>
): SetTotals {
  const n = (id: RegionId) => counts[id] ?? 0;
  const ids = REGION_IDS[setCount];
  const sets = [0, 1, 2]
    .slice(0, setCount)
    .map((index) =>
      ids
        .filter((id) => REGION_SETS[id].includes(index))
        .reduce((sum, id) => sum + n(id), 0)
    );
  const all = setCount === 3 ? n("abc") : 0;
  return {
    sets,
    pairs: {
      ab: n("ab") + all,
      ac: n("ac") + all,
      bc: n("bc") + all,
    },
    all,
    union: ids.reduce((sum, id) => sum + n(id), 0),
  };
}
